/**
 * Loans offered to this account, as the BORROWER reads them.
 *
 * The lender's side — proposing a loan, sending it out, cancelling it — is
 * `src/estate/`'s. This is the other party looking at the same rows, and the
 * whole of OND-9: until it existed a loan carried the lender's account of
 * itself, opening when they said so and closing when they said so.
 *
 * WHICH CONTROLS A ROW OFFERS IS DECIDED HERE AND MIRRORS THE SQL, clause for
 * clause, in the register `RailRow.cancellableLoanId` set: the database decides,
 * always. `ondo.accept_loan()` and `decline_loan()` admit 'proposed';
 * `mark_loan_received()` admits 'out' with no receipt yet;
 * `mark_loan_returned()` admits 'out'. So a row offers exactly what would be
 * accepted, and a refusal still renders if the two ever disagree.
 *
 * THERE IS NO CLOCK IN THIS FILE, and unlike its neighbour that is not an
 * incidental absence. `transferOffers.model.ts` takes a `now` because an expired
 * covenant is no covenant; nothing here takes one at all. No row's controls
 * depend on what day it is — a loan whose `endsOn` passed last month still
 * offers exactly the same buttons — because OND-9 forbids marking physical
 * receipt or return from a timer, and a screen that hid or pressed a button on a
 * date would be that timer wearing a different hat. Dates are rendered and never
 * compared.
 */

import type { BorrowedLoan } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatDay } from "../lib/dates.ts";
import { BORROWED_COPY } from "./loans.constants.ts";

/** The four things a borrower can do, and nothing else. */
export type BorrowedLoanAct = "accept" | "decline" | "received" | "returned";

/** One loan, with every label and every control already decided. */
export interface BorrowedLoanRow {
  loanId: string;
  workId: string;
  title: string;
  /** Where it is going, which read by the borrower is their own address. */
  venue: string;
  /** 'Erwin Wurm Studio · ONDO-4001', or the sentence saying it is not shown. */
  lender: string;
  /** 'from 01 Sep 2026 until 01 Dec 2026', or the sentence saying none. */
  span: string;
  purpose: string;
  /** What this state means to the borrower, not to the lender. */
  state: string;
  /** 'You recorded this as received on 04 Feb 2026.' Null until they have. */
  receipt: string | null;
  /**
   * The acts this row offers, in the order they appear. Empty is a real state
   * and `waiting` is then the sentence explaining it.
   */
  acts: BorrowedLoanAct[];
  /** Why there is nothing to press. Null when there is. */
  waiting: string | null;
  /**
   * The work's own record. Readable while a proposal is open — which is OND-9's
   * other half, since a museum cannot decide about a picture it cannot see —
   * and not after it has been declined or returned.
   */
  href: string;
}

export type BorrowedLoansView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | { kind: "ready"; rows: BorrowedLoanRow[]; summary: string };

/**
 * Everything the screen renders, decided here.
 *
 * A discriminated union rather than a bag of flags, so "loading and also
 * errored" cannot be represented — the shape every screen in this app uses.
 */
export function toBorrowedLoansView(input: {
  loans: BorrowedLoan[] | null;
  error: string | null;
  workBase: string;
}): BorrowedLoansView {
  const { loans, error, workBase } = input;

  if (error) return { kind: "error", title: BORROWED_COPY.errorTitle, message: error };
  if (!loans) return { kind: "loading", message: BORROWED_COPY.loading };

  if (loans.length === 0) {
    return {
      kind: "empty",
      message: BORROWED_COPY.empty,
      detail: BORROWED_COPY.emptyDetail,
    };
  }

  return {
    kind: "ready",
    rows: loans.map((loan) => toRow(loan, workBase)),
    summary: BORROWED_COPY.count(loans.length),
  };
}

function toRow(loan: BorrowedLoan, workBase: string): BorrowedLoanRow {
  const acts = actsFor(loan);

  return {
    loanId: loan.loanId,
    workId: loan.workId,
    title: loan.title,
    venue: loan.venueText,
    lender: describeLender(loan),
    span: describeSpan(loan),
    purpose: loan.purposeText ?? BORROWED_COPY.noPurpose,
    state: BORROWED_COPY.loanStates[loan.status],
    receipt: loan.receivedAt
      ? BORROWED_COPY.receivedOn(formatDay(loan.receivedAt))
      : null,
    acts,
    waiting: acts.length === 0 ? waitingFor(loan) : null,
    href: `${workBase}/${loan.workId}`,
  };
}

/**
 * Which of the four acts this loan admits, mirroring each definer's `where`.
 *
 * RECEIPT AND RETURN ARE BOTH OFFERED ON A LOAN THAT IS OUT, and that is the
 * database's answer rather than a convenience. `ondo.mark_loan_returned()`
 * requires 'out' and says nothing about a receipt, so a museum that forgot to
 * record the arrival and is now sending the work back is not trapped — the
 * record simply ends with no receipt on it, which is an ordinary gap rather
 * than a contradiction. Hiding Return until Receipt was pressed would have the
 * screen refusing something the server allows, which is the one direction this
 * app never takes.
 */
export function actsFor(loan: BorrowedLoan): BorrowedLoanAct[] {
  if (loan.status === "proposed") return ["accept", "decline"];

  if (loan.status === "out") {
    return loan.receivedAt === null ? ["received", "returned"] : ["returned"];
  }

  return [];
}

/**
 * Why there is nothing to press, which is never left blank.
 *
 * 'agreed' is the state worth the sentence: the museum has said yes and the
 * work has not been sent, so a screen with no controls and no explanation would
 * read as broken rather than as waiting. Everything else here is over.
 */
export function waitingFor(loan: BorrowedLoan): string {
  return loan.status === "agreed"
    ? BORROWED_COPY.waitingToBeSent
    : BORROWED_COPY.finished;
}

/**
 * The lender, named plainly when they cannot be resolved.
 *
 * Null is a real answer and not missing data: the lender is whoever holds an
 * active owner grant on the collection the work is live-placed in, resolved two
 * joins out in SQL, and a work whose placement has ended has no owner to name.
 * A blank would read as a bug.
 */
export function describeLender(loan: BorrowedLoan): string {
  if (!loan.lenderOndoId || !loan.lenderName) return BORROWED_COPY.lenderUnknown;
  return `${loan.lenderName} · ${loan.lenderOndoId}`;
}

/**
 * 'from 01 Sep 2026 until 01 Dec 2026', with either end allowed to be absent.
 *
 * Both dates are optional on `ondo.loans` and an open-ended loan is a real
 * arrangement, so a missing end is not rendered as a gap — the sentence just
 * stops. Neither date gates a control: see this file's header.
 */
export function describeSpan(loan: BorrowedLoan): string {
  const parts = [
    loan.startsOn ? `${BORROWED_COPY.from} ${formatDay(loan.startsOn)}` : null,
    loan.endsOn ? `${BORROWED_COPY.until} ${formatDay(loan.endsOn)}` : null,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? BORROWED_COPY.noDates : parts.join(" ");
}

/**
 * What went wrong, in words a registrar can act on.
 *
 * A 403 or a 404 on any of the four acts is almost always one thing: the row
 * moved while the screen was open. Every door requires a specific status, so a
 * lender withdrawing the proposal — or a colleague answering in another tab —
 * turns the loan into something that no longer exists to answer. The server's
 * own sentence is preferred whenever there is one.
 */
export function describeBorrowedError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.isUnreachable) return cause.message;
    if (cause.status === 403 || cause.status === 404) return BORROWED_COPY.errors.gone;
    if (cause.message) return cause.message;

    return BORROWED_COPY.errors.refused;
  }

  return cause instanceof Error ? cause.message : BORROWED_COPY.errors.refused;
}
